import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Paper,
  Checkbox,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack
} from "@mui/material";
import {
  Download,
  ContentCopy,
  Check,
  CheckCircle,
  ExpandMore,
  InstallDesktop,
  Terminal,
  Security,
  HelpOutline,
  Storage,
  WarningAmber,
  Sync
} from "@mui/icons-material";
import { apiServer, urlBase } from "../../api";
import { useLogin } from "../../contexts/LoginContext";

interface DadosInstalacao {
  sucesso: boolean;
  tokenInstalacao: string;
  clienteID: string;
  codigo: number;
  nomeCliente: string;
  statusInstalacao: "instalado" | "pendente";
  totalTarefas: number;
  totalArquivos: number;
  ultimoBackup?: string | null;
  urlDownloadInstalador: string;
  versaoInstalador: string;
  tamanhoInstaladorMB: string;
}

const PASSOS_CHECKLIST = [
  {
    id: 1,
    titulo: "1. Baixar o Pacote do Instalador Oficial",
    descricao: "Faça o download do pacote oficial do Backup Fácil para o servidor ou computador onde estão instalados seus bancos de dados.",
    detalhe: "O download contém o Instalador gráfico (.exe), bibliotecas nativas de alto desempenho e o serviço em segundo plano."
  },
  {
    id: 2,
    titulo: "2. Executar como Administrador",
    descricao: "Descompacte o arquivo baixado (.7z ou .zip) e execute o arquivo BackupFacil.Instalador.exe com privilégios de Administrador.",
    detalhe: "Clique com o botão direito no arquivo executável e escolha a opção 'Executar como Administrador' para permitir a instalação do serviço do Windows."
  },
  {
    id: 3,
    titulo: "3. Informar o Token de Instalação",
    descricao: "Na tela de ativação, insira o seu Token exclusivo de Instalação para vincular a máquina à sua conta.",
    detalhe: "O instalador conectará via HTTPS seguro e configurará suas tarefas com criptografia automática de 256 bits."
  },
  {
    id: 4,
    titulo: "4. Selecionar Bancos Detectados e Horários",
    descricao: "Confirme os bancos de dados detectados automaticamente (ou aponte para o ERP) e defina a frequência diária de backups.",
    detalhe: "Para bancos locais, informe a senha de acesso (ex: postgres, sa ou masterkey) para permitir a execução autônoma dos dumps."
  },
  {
    id: 5,
    titulo: "5. Concluir e Verificar o Serviço Ativo",
    descricao: "Finalize a instalação. O serviço BackupFacil.Client será instalado e iniciado automaticamente no Windows.",
    detalhe: "Após alguns minutos, você já verá o status de conexão atualizado aqui no Portal e nos gráficos do seu Dashboard."
  }
];

export default function InstalacaoPage() {
  const [login] = useLogin();
  const clienteID = login.usuario?.usuarioCliente?.clienteID;

  const [dados, setDados] = useState<DadosInstalacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [tokenCopiado, setTokenCopiado] = useState(false);

  // Checklist interativo persistido no localStorage
  const storageKey = `bf_checklist_instalacao_${clienteID || 'default'}`;
  const [passosConcluidos, setPassosConcluidos] = useState<number[]>(() => {
    try {
      const salvo = localStorage.getItem(storageKey);
      return salvo ? JSON.parse(salvo) : [];
    } catch {
      return [];
    }
  });

  const carregarDadosInstalacao = useCallback(async () => {
    if (!clienteID || !login.accessToken) {
      setCarregando(false);
      return;
    }

    setCarregando(true);
    setErro("");

    try {
      const response = await apiServer<DadosInstalacao>().get(
        `v2/cliente/instalacao?clienteID=${clienteID}`,
        login.accessToken
      );
      setDados(response.data);

      // Se o cliente já tem tarefas ou backups concluídos, marca automaticamente passos 1 a 5
      if (response.data.statusInstalacao === "instalado") {
        setPassosConcluidos([1, 2, 3, 4, 5]);
        try {
          localStorage.setItem(storageKey, JSON.stringify([1, 2, 3, 4, 5]));
        } catch {}
      }
    } catch (err: any) {
      console.error("Erro ao carregar dados de instalação:", err);
      setErro(err.response?.data?.mensagem || "Não foi possível carregar os dados de instalação.");
    } finally {
      setCarregando(false);
    }
  }, [clienteID, login.accessToken, storageKey]);

  useEffect(() => {
    carregarDadosInstalacao();
  }, [carregarDadosInstalacao]);

  const alternarPasso = (id: number) => {
    setPassosConcluidos((prev) => {
      const novo = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
      try {
        localStorage.setItem(storageKey, JSON.stringify(novo));
      } catch {}
      return novo;
    });
  };

  const copiarToken = async () => {
    if (!dados?.tokenInstalacao) return;
    try {
      await navigator.clipboard.writeText(dados.tokenInstalacao);
      setTokenCopiado(true);
      setTimeout(() => setTokenCopiado(false), 3000);
    } catch (err) {
      console.error("Erro ao copiar token:", err);
    }
  };

  const iniciarDownload = () => {
    const downloadUrl = `${urlBase}/v2/instalador/download`;
    window.location.href = downloadUrl;
  };

  const progressoPercentual = Math.round((passosConcluidos.length / PASSOS_CHECKLIST.length) * 100);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Cabeçalho da Página */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 2 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <InstallDesktop sx={{ fontSize: 32, color: "primary.main" }} />
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em">
              Instalação do Agente Backup Fácil
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Guia oficial de implantação, seu Token exclusivo de ativação e checklist de configuração.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<Sync />}
          onClick={carregarDadosInstalacao}
          disabled={carregando}
          sx={{ textTransform: "none" }}
        >
          Atualizar Status
        </Button>
      </Box>

      {carregando ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
          <CircularProgress size={44} />
          <Typography variant="body2" color="text.secondary" mt={2}>
            Carregando dados e status de instalação...
          </Typography>
        </Box>
      ) : erro ? (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={carregarDadosInstalacao}>Tentar Novamente</Button>}>
          {erro}
        </Alert>
      ) : dados ? (
        <>
          {/* Card de Destaque: Token de Instalação e Download */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "primary.main",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(15, 23, 42, 0.6) 100%)"
                  : "linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(248, 250, 252, 1) 100%)",
              boxShadow: "0 4px 20px rgba(37, 99, 235, 0.08)"
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Chip
                    label={dados.statusInstalacao === "instalado" ? "Agente Ativo e Sincronizado" : "Aguardando Primeira Instalação"}
                    color={dados.statusInstalacao === "instalado" ? "success" : "warning"}
                    size="small"
                    icon={dados.statusInstalacao === "instalado" ? <CheckCircle /> : <WarningAmber />}
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Cliente #{dados.codigo} • {dados.nomeCliente}
                  </Typography>
                </Box>

                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Seu Token de Instalação Exclusivo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Copie o código abaixo e informe no instalador do Backup Fácil para vincular seu servidor automaticamente:
                </Typography>

                {/* Caixa do Token */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "background.paper",
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    border: "2px dashed",
                    borderColor: "primary.main",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                  }}
                >
                  <Typography
                    variant="h5"
                    fontFamily="monospace"
                    fontWeight={800}
                    letterSpacing="0.1em"
                    color="primary.main"
                    sx={{ userSelect: "all" }}
                  >
                    {dados.tokenInstalacao}
                  </Typography>

                  <Tooltip title={tokenCopiado ? "Copiado!" : "Copiar Token"}>
                    <Button
                      variant={tokenCopiado ? "contained" : "outlined"}
                      color={tokenCopiado ? "success" : "primary"}
                      size="small"
                      startIcon={tokenCopiado ? <Check /> : <ContentCopy />}
                      onClick={copiarToken}
                      sx={{ textTransform: "none", minWidth: 100 }}
                    >
                      {tokenCopiado ? "Copiado!" : "Copiar"}
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>

              {/* Botão de Download do Instalador */}
              <Grid item xs={12} md={5} textAlign={{ xs: "left", md: "right" }}>
                <Box sx={{ bgcolor: "background.paper", p: 2.5, borderRadius: 2, display: "inline-block", textAlign: "center", width: "100%", maxWidth: 360, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Instalador Oficial para Windows
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Versão {dados.versaoInstalador} • Windows 64-bit • {dados.tamanhoInstaladorMB}
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={<Download />}
                    onClick={iniciarDownload}
                    sx={{
                      py: 1.2,
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)"
                    }}
                  >
                    Baixar Instalador
                  </Button>

                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Instalação rápida em menos de 3 minutos
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Checklist Interativo de Instalação */}
          <Card sx={{ mb: 4, borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircle color="primary" />
                  <Typography variant="h6" fontWeight={700}>
                    Checklist de Instalação Passo a Passo
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} color="primary.main">
                  {passosConcluidos.length} de {PASSOS_CHECKLIST.length} passos ({progressoPercentual}%)
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progressoPercentual}
                sx={{ height: 8, borderRadius: 4, mb: 3 }}
              />

              <Stack spacing={2}>
                {PASSOS_CHECKLIST.map((passo) => {
                  const concluido = passosConcluidos.includes(passo.id);
                  return (
                    <Paper
                      key={passo.id}
                      elevation={0}
                      onClick={() => alternarPasso(passo.id)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: concluido ? "success.main" : "divider",
                        bgcolor: concluido ? "action.hover" : "background.paper",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": { borderColor: "primary.main" }
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={1.5}>
                        <Checkbox
                          checked={concluido}
                          color="success"
                          sx={{ p: 0.5, mt: 0.25 }}
                          onChange={() => alternarPasso(passo.id)}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              textDecoration: concluido ? "line-through" : "none",
                              color: concluido ? "text.secondary" : "text.primary"
                            }}
                          >
                            {passo.titulo}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {passo.descricao}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                            💡 {passo.detalhe}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          {/* Manual Operacional Completo e FAQ */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HelpOutline color="action" />
              <Typography variant="h6" fontWeight={700}>
                Manual de Instalação e Dúvidas Frequentes
              </Typography>
            </Box>

            <Accordion defaultExpanded sx={{ borderRadius: 2, mb: 1, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Terminal color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Requisitos Mínimos do Sistema Operacional
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  O agente do Backup Fácil é compatível com os seguintes ambientes Windows (arquitetura 64-bit):
                </Typography>
                <ul style={{ paddingLeft: 20, margin: 0, color: "#475569", fontSize: "0.875rem" }}>
                  <li>Windows Server 2012, 2012 R2, 2016, 2019, 2022 ou 2025.</li>
                  <li>Windows 10 e Windows 11 (edições Pro, Enterprise ou Server).</li>
                  <li>Mínimo de 200 MB livres de espaço em disco para a aplicação.</li>
                  <li>Privilégios de Administrador local no computador para instalar o serviço do Windows.</li>
                  <li>Acesso à internet via HTTPS padrão (porta de saída 443).</li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ borderRadius: 2, mb: 1, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Storage color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Integração Rápida com ERP Data7 e Bancos de Dados
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  O instalador possui detecção automática do arquivo <code>Data7.Config</code> utilizado pelo ERP Data7 (Se7e Sistemas).
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  1. O instalador busca automaticamente instalações em <code>C:\Data7\...</code> e carrega todos os bancos configurados.<br />
                  2. Você pode clicar no botão <strong>Procurar...</strong> para selecionar manualmente outro arquivo de configuração caso o ERP esteja em outro drive.<br />
                  3. Selecione os bancos desejados, informe as senhas de conexão e escolha os horários de execução diária (ex: 12:00, 18:00, 22:00).
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ borderRadius: 2, mb: 1, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Security color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Configurações de Antivírus e Firewall Corporativo
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Para evitar falsos positivos de antivírus e garantir a execução contínua dos backups em segundo plano:
                </Typography>
                <ul style={{ paddingLeft: 20, margin: 0, color: "#475569", fontSize: "0.875rem" }}>
                  <li>Adicione a pasta <code>C:\BackupFacil</code> na lista de exclusões do Windows Defender / Antivírus.</li>
                  <li>Certifique-se de que o firewall permita conexões de saída na porta TCP <strong>443 (HTTPS)</strong> para o domínio <code>srvbkp.backupfacil.com</code>.</li>
                  <li>O serviço do Windows é executado como <code>LocalSystem</code>, garantindo permissão de leitura nos diretórios de backup.</li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <HelpOutline color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Onde Encontrar os Arquivos de Log do Instalador
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Todos os passos e diagnósticos de execução são registrados em logs locais com timestamps detalhados:
                </Typography>
                <ul style={{ paddingLeft: 20, margin: 0, color: "#475569", fontSize: "0.875rem" }}>
                  <li><strong>Log do Instalador:</strong> <code>C:\BackupFacil\Log\Instalador.log</code></li>
                  <li><strong>Log do Serviço de Backup:</strong> <code>C:\BackupFacil\Log\Client.log</code></li>
                </ul>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  Em caso de dúvidas técnicas durante a instalação, acione o suporte técnico fornecendo esses arquivos para atendimento imediato.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
